import { useTranslation } from 'react-i18next';
import { Info } from '../../../assets/icons';
import { IconSize } from '../../../constant/IconSize.constant';
import { Charger } from '../../../stores/types';
import { TRAILER_ACCESSIBILITY } from '../../../stores/types/chargers.interface';
import { ColorType, Dropdown, FormInput, Icon, Label, LabelType } from '../../_ui';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../_ui/Tooltip.component';
import { transformTrailerAccessibilityToString } from './utils';

type AdditionalInfoProps = {
  charger: Charger;
  editEnabled: boolean;
  isLoading: boolean;
  handleInputChange: any;
  updateChargerDetail: any;
};

export const AdditionalInfo = ({
  charger,
  editEnabled,
  isLoading,
  handleInputChange,
  updateChargerDetail,
}: AdditionalInfoProps) => {
  const { t } = useTranslation();

  const trailerAccessibleDropdownForPort = (portTrailerAccessibility: TRAILER_ACCESSIBILITY) => {
    return Object.values(TRAILER_ACCESSIBILITY).map((item: string, index: number) => ({
      id: index,
      label: t(transformTrailerAccessibilityToString(item as TRAILER_ACCESSIBILITY)),
      value: item,
      selected: item === portTrailerAccessibility,
    }));
  };

  const renderTrailerAccessibleReadOnly = () => {
    return (
      <div className='flex flex-row items-start min-h-[40px]'>
        <Label
          text={t('trailer_accessible')}
          type={LabelType.LABEL_S}
          color={ColorType.GREY6}
          className='min-w-[148px] max-w-[148px] mr-2.5'
        />
        <div className='flex flex-col'>
          {charger?.ports?.map((port) => {
            return (
              <div
                key={`trailer-accessability-read-only-port-${port.id}`}
                className='flex flex-row gap-2 items-start h-10'
              >
                {charger?.ports?.length > 1 && (
                  <div className='flex flex-col gap-0.5'>
                    <Label
                      text={`${t(port.physicalLocation.toLowerCase())} ${t('port').toLowerCase()}:`}
                      type={LabelType.BODY3}
                      color={ColorType.GREY6}
                    />
                  </div>
                )}
                <div className='flex flex-col gap-0.5'>
                  <Label
                    text={t(
                      transformTrailerAccessibilityToString(
                        port?.trailerAccessibility || TRAILER_ACCESSIBILITY.NOT_INDICATED,
                      ),
                    )}
                    type={LabelType.BODY3}
                    color={ColorType.BLACK}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderTrailerAccessibleEdit = () => {
    return (
      <div>
        {charger?.ports?.map((port, i) => {
          return (
            <div
              key={`trailer-accessability-edit-port-${port.id}`}
              className='flex flex-row items-center min-h-[40px]'
            >
              <Label
                text={
                  charger?.ports?.length > 1
                    ? `${t('trailers')} (${t(port.physicalLocation.toLowerCase())} ${t(
                        'port',
                      ).toLowerCase()})`
                    : `${t('trailers')}`
                }
                type={LabelType.LABEL_S}
                color={ColorType.GREY6}
                className='min-w-[148px] max-w-[148px] mr-2.5'
              />
              <div className='flex-1'>
                <Dropdown
                  items={trailerAccessibleDropdownForPort(
                    updateChargerDetail?.trailerAccessibility[`${charger.id}.${i + 1}`] ||
                      TRAILER_ACCESSIBILITY.NOT_INDICATED,
                  )}
                  onItemClick={(selectedItem: any) => {
                    const currentTrailerAccessibility = {
                      ...updateChargerDetail?.trailerAccessibility,
                      [`${charger.id}.${i + 1}`]: selectedItem.value,
                    };

                    handleInputChange('trailerAccessibility', currentTrailerAccessibility);
                  }}
                  headerWidth='100%'
                  labelType={LabelType.BODY3_G4}
                  labelTypeHighlighted={LabelType.DROPDOWN_HEADER}
                  chevdownIconHighlightColor={ColorType.GREY6}
                  headerHighLightClassName='bg-grey1 border-grey3 rounded h-[36px] pr-2'
                  headerClassName='bg-grey1 border-grey3 rounded pr-2'
                  headerHeight='36px'
                  dataTestId={`chargerTrailerAccessibility-${i}`}
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  };
  return (
    <div className={`flex flex-col gap-0 pt-4 ${editEnabled ? 'pb-3' : ''}`}>
      <Label
        text={t('charger_additional_info')}
        type={LabelType.LABEL_M}
        color={ColorType.GREY5}
        style={{ minHeight: '40px' }}
      />
      <div className={`flex flex-col ${editEnabled ? 'gap-1' : 'gap-0'}`}>
        <div
          className={`flex flex-row ${editEnabled ? 'items-center' : 'items-start'}  min-h-[40px]`}
        >
          <div className='flex flex-row gap-1 min-w-[148px] max-w-[148px] mr-2.5 items-start'>
            <Label
              text={t('charger_directions')}
              type={LabelType.LABEL_S}
              color={ColorType.GREY6}
            />
            <Tooltip>
              <TooltipTrigger>
                <Icon src={Info} size={IconSize.SIZE_20x20} />
              </TooltipTrigger>
              <TooltipContent>
                <Label text={t('charger_tip')} type={LabelType.BODY3} color={ColorType.WHITE} />
              </TooltipContent>
            </Tooltip>
          </div>
          {!editEnabled && (
            <Label
              text={charger?.directions}
              type={LabelType.BODY3}
              color={ColorType.BLACK}
              className='text-ellipsis overflow-hidden text-justify'
              isLoading={isLoading}
              skeletonWidth='w-52'
              skeletonHeight='h-5'
            />
          )}
          {editEnabled && (
            <div className='flex-1'>
              <FormInput
                defaultValue={charger?.directions}
                onChange={(event) => handleInputChange('directions', event.target.value)}
                width='100%'
                height='36px'
              />
            </div>
          )}
        </div>
        <div
          className={`flex flex-row ${editEnabled ? 'items-center' : 'items-start'}  min-h-[40px]`}
        >
          <Label
            text={t('charger_parking_space')}
            type={LabelType.LABEL_S}
            color={ColorType.GREY6}
            className='min-w-[148px] max-w-[148px] mr-2.5'
          />
          <div className='flex-1'>
            {!editEnabled && (
              <Label
                text={charger?.parkingSpot}
                type={LabelType.BODY3}
                color={ColorType.BLACK}
                className='text-ellipsis overflow-hidden text-justify'
                isLoading={isLoading}
                skeletonWidth='w-52'
                skeletonHeight='h-5'
              />
            )}
            {editEnabled && (
              <FormInput
                defaultValue={charger?.parkingSpot}
                onChange={(event) => handleInputChange('parkingSpot', event.target.value)}
                width='100%'
                height='36px'
              />
            )}
          </div>
        </div>
        <div
          className={`flex flex-row ${editEnabled ? 'items-center' : 'items-start'}  min-h-[40px]`}
        >
          <Label
            text={t('charger_unit')}
            type={LabelType.LABEL_S}
            color={ColorType.GREY6}
            className='min-w-[148px] max-w-[148px] mr-2.5'
          />
          <div className='flex-1'>
            {!editEnabled && (
              <Label
                text={charger?.associatedBuildingUnit}
                type={LabelType.BODY3}
                color={ColorType.BLACK}
                className={`${
                  !isLoading ? 'w-[190px]' : ''
                } text-ellipsis overflow-hidden text-justify`}
                isLoading={isLoading}
                skeletonWidth='w-52'
                skeletonHeight='h-5'
              />
            )}
            {editEnabled && (
              <div className='flex-1'>
                <FormInput
                  defaultValue={charger?.associatedBuildingUnit}
                  onChange={(event) =>
                    handleInputChange('associatedBuildingUnit', event.target.value)
                  }
                  width='100%'
                  height='36px'
                />
              </div>
            )}
          </div>
        </div>
        <div
          className={`flex flex-row ${editEnabled ? 'items-center' : 'items-start'}  min-h-[40px]`}
        >
          <div className='flex flex-row gap-1 min-w-[148px] max-w-[148px] mr-2.5 items-start'>
            <Label
              text={t('charger_internal_note')}
              type={LabelType.LABEL_S}
              color={ColorType.GREY6}
            />
          </div>
          <div className='flex-1'>
            {!editEnabled && (
              <Label
                text={charger?.internalNote}
                type={LabelType.BODY3}
                color={ColorType.BLACK}
                className={`${
                  !isLoading ? 'w-[190px]' : ''
                } text-ellipsis overflow-hidden text-justify`}
                isLoading={isLoading}
                skeletonWidth='w-52'
                skeletonHeight='h-5'
              />
            )}
            {editEnabled && (
              <FormInput
                defaultValue={charger?.internalNote}
                onChange={(event) => handleInputChange('internalNote', event.target.value)}
                width='100%'
                height='36px'
              />
            )}
          </div>
        </div>
        {!editEnabled && renderTrailerAccessibleReadOnly()}
        {editEnabled && renderTrailerAccessibleEdit()}
      </div>
    </div>
  );
};
